import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { NodeProps } from 'reactflow';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CANVAS_GROUP_BODY_PADDING, CANVAS_GROUP_HEADER_HEIGHT } from '../layoutConstants';
import type { CanvasNodeData, JoinType } from '../types';
import type { FieldMapping } from '../../services/api';
import TableChartIcon from '@mui/icons-material/TableChart';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PreviewIcon from '@mui/icons-material/Preview';
import BuildIcon from '@mui/icons-material/Build';
import LinkIcon from '@mui/icons-material/Link';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FunctionsIcon from '@mui/icons-material/Functions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export const joinTypeColor = (joinType: string): string => {
  switch (joinType.toLowerCase()) {
    case 'inner': return '#2563eb'; // Blue 600
    case 'left': return '#3b82f6';  // Blue 500
    case 'right': return '#0ea5e9'; // Sky 500
    case 'full': return '#1d4ed8';  // Blue 700
    case 'cross': return '#f59e0b'; // Amber
    default: return '#2563eb';
  }
};

const accentForKind = (kind: CanvasNodeData['kind']): string => {
  switch (kind) {
    case 'sourceField':
      return '#10b981'; // Emerald 500 (Green)
    case 'targetField':
      return '#2563eb'; // Blue 600
    case 'transformation':
      return '#3b82f6'; // Blue 500
    case 'aggregation':
      return '#0ea5e9'; // Sky 500
    case 'linkBridge':
      return '#334155'; // Slate 700
    case 'validation':
      return '#f97316'; // Orange 500
    case 'lookup':
      return '#14b8a6'; // Teal 500
    case 'joinNode':
      return '#1d4ed8'; // Blue 700
    case 'tableNode':
      return '#2563eb'; // Blue 600
    case 'intermediateField':
    default:
      return '#64748b'; // Slate 500
  }
};

const nodeMinHeight = (kind: CanvasNodeData['kind'], compact?: boolean): number => {
  if (compact) {
    return 44;
  }
  if (kind === 'transformation' || kind === 'aggregation') {
    return 64;
  }
  return 52;
};

const circleHandleStyle = (side: 'left' | 'right', color: string) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: '2px solid #fff',
  backgroundColor: color,
  boxShadow: `0 0 0 2px ${alpha(color, 0.2)}`,
  [side]: -12,
  top: '50%',
  transform: 'translateY(-50%)',
  transition: 'transform 0.15s, box-shadow 0.15s',
});

const extractFieldType = (data: CanvasNodeData): string | undefined => {
  const meta = data.metadata as Record<string, unknown> | undefined;
  const mappingMeta = (data.mapping as unknown as { metadata?: Record<string, unknown> } | undefined)?.metadata;
  const potential = [
    meta?.dataType,
    meta?.datatype,
    meta?.fieldType,
    meta?.type,
    mappingMeta?.target_type,
    mappingMeta?.source_type,
  ];
  return potential.find((value): value is string => typeof value === 'string' && value.length > 0);
};

const FieldRow: React.FC<{
  data: CanvasNodeData;
  selected?: boolean;
  orientation: 'source' | 'target';
}> = ({ data, selected, orientation }) => {
  const accent = accentForKind(data.kind);
  const fieldType = extractFieldType(data);
  const required = orientation === 'target' && Boolean(data.mapping?.is_required);
  const mapping = data.mapping as (Partial<FieldMapping> & { column_mapping?: Record<string, unknown> }) | undefined;
  const rawFieldName = useMemo(() => {
    const columnMapping = mapping?.column_mapping as Record<string, unknown> | undefined;
    const explicitField = data.field && data.field.trim().length > 0 ? data.field.trim() : undefined;
    const mappingField = orientation === 'source'
      ? (typeof mapping?.source_field === 'string' && mapping.source_field.trim().length > 0 ? mapping.source_field.trim() : undefined)
      : (typeof mapping?.target_field === 'string' && mapping.target_field.trim().length > 0 ? mapping.target_field.trim() : undefined);
    const nestedField = orientation === 'source'
      ? (typeof columnMapping?.source_column === 'string' && columnMapping.source_column.trim().length > 0 ? columnMapping.source_column.trim() : undefined)
      : (typeof columnMapping?.target_column === 'string' && columnMapping.target_column.trim().length > 0 ? columnMapping.target_column.trim() : undefined);
    const labelField = data.label && data.label.trim().length > 0 ? data.label.trim() : undefined;

    return explicitField || mappingField || nestedField || labelField || '';
  }, [data.field, data.label, mapping, orientation]);
  const displayName = rawFieldName || (orientation === 'source' ? 'Source column missing' : 'Target column missing');
  const isPlaceholder = rawFieldName.length === 0;

  // Validation badge
  const hints = data.validationHints || [];
  const errorCount = hints.filter(h => h.severity === 'error').length;
  const warningCount = hints.filter(h => h.severity === 'warning').length;
  const issueCount = errorCount + warningCount;
  const highestSeverity = errorCount > 0 ? 'error' : warningCount > 0 ? 'warning' : null;
  const badgeColor = highestSeverity === 'error' ? '#ef4444' : highestSeverity === 'warning' ? '#f59e0b' : undefined;
  const dotColor = data.isUnmapped ? '#f59e0b'
    : highestSeverity === 'error' ? '#ef4444'
    : highestSeverity === 'warning' ? '#f59e0b'
    : accent;

  return (
    <Box sx={{ width: '100%' }}>
      <Handle
        type={orientation === 'source' ? 'source' : 'target'}
        position={orientation === 'source' ? Position.Right : Position.Left}
        style={circleHandleStyle(orientation === 'source' ? 'right' : 'left', required ? '#e11d48' : accent)}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: dotColor,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: data.isUnmapped ? 'text.disabled' : selected ? accent : isPlaceholder ? 'text.secondary' : 'text.primary',
              opacity: data.isUnmapped ? 0.7 : 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={displayName}
          >
            {displayName}{mapping?.approved_at ? ' ✓' : ''}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {issueCount > 0 && badgeColor && (
            <Tooltip
              title={
                <Box>
                  {hints.slice(0, 3).map((h, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ fontSize: 11 }}>
                      {h.severity === 'error' ? '⛔' : '⚠️'} {h.message}
                    </Typography>
                  ))}
                  {hints.length > 3 && (
                    <Typography variant="caption" display="block" sx={{ fontSize: 11, fontStyle: 'italic' }}>
                      +{hints.length - 3} more
                    </Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '9px',
                  backgroundColor: alpha(badgeColor, 0.12),
                  border: `1px solid ${alpha(badgeColor, 0.35)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: badgeColor,
                    lineHeight: 1,
                  }}
                >
                  {issueCount}
                </Typography>
              </Box>
            </Tooltip>
          )}
          {fieldType && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {fieldType}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const NodeContainer: React.FC<{
  nodeId?: string;
  selected?: boolean;
  kind: CanvasNodeData['kind'];
  children: React.ReactNode;
  compact?: boolean;
  data?: CanvasNodeData;
}> = ({ nodeId, selected, kind, children, compact, data }) => {
  const accent = useMemo(() => accentForKind(kind), [kind]);
  const isFieldNode = kind === 'sourceField' || kind === 'targetField';
  const isTransformation = kind === 'transformation' || kind === 'aggregation';
  const isIntermediate = kind === 'intermediateField';
  const aiGenerated = data?.aiGenerated || data?.provenance?.origin === 'llm';

  const horizontalPadding = isFieldNode ? 12 : compact ? 14 : 16;
  const verticalPadding = compact ? 8 : isTransformation ? 12 : 10;
  return (
    <Box
      data-id={nodeId}
      data-kind={kind}
      sx={{
        position: 'relative',
        width: isFieldNode ? '100%' : 'auto',
        minHeight: nodeMinHeight(kind, compact),
        borderRadius: 1, // Uses theme.shape.borderRadius (12px)
        border: '1px solid',
        borderColor: selected ? accent : alpha('#1f2937', data?.isUnmapped ? 0.03 : 0.06),
        backgroundColor: '#ffffff',
        opacity: data?.isUnmapped ? 0.6 : 1,
        boxShadow: selected
          ? `0 10px 25px -5px ${alpha(accent, 0.15)}, 0 0 0 2px ${accent}`
          : '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
        minWidth: isFieldNode ? undefined : isTransformation ? 208 : isIntermediate ? 196 : undefined,
        maxWidth: '100%',
        padding: `${verticalPadding}px ${horizontalPadding}px`,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: isTransformation ? 0.4 : 0.5,
        pointerEvents: 'auto',
        transition: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: `0 10px 20px -5px ${alpha(accent, 0.1)}, 0 4px 6px -2px rgba(0, 0, 0, 0.05)`,
          borderColor: alpha(accent, 0.5),
        },
      }}
    >
      {aiGenerated && (
        <Tooltip title="Generated by AI" placement="left" arrow>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: alpha(accent, 0.18),
              border: `1px solid ${alpha(accent, 0.45)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 4,
              boxShadow: `0 6px 12px ${alpha('#0f172a', 0.16)}`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16, color: accent }} />
          </Box>
        </Tooltip>
      )}
      {children}
    </Box>
  );
};

export const SourceFieldNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => (
  <NodeContainer nodeId={id} selected={selected} kind={data.kind} data={data}>
    <FieldRow data={data} selected={selected} orientation="source" />
  </NodeContainer>
));

export const TargetFieldNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => (
  <NodeContainer nodeId={id} selected={selected} kind={data.kind} data={data}>
    <FieldRow data={data} selected={selected} orientation="target" />
  </NodeContainer>
));

export const TransformationNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const accent = accentForKind('transformation');
  const expression = typeof data.metadata?.expression === 'string' ? data.metadata.expression : '';
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    setDialogOpen(true);
  };

  const handleDialogClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (typeof (event as { stopPropagation?: () => void }).stopPropagation === 'function') {
      (event as { stopPropagation?: () => void }).stopPropagation?.();
    }
    if (reason === 'backdropClick') {
      setDialogOpen(false);
      return;
    }
    setDialogOpen(false);
  };

  const handleDialogDismiss = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    setDialogOpen(false);
  };

  const inputs = data.step?.inputs ?? [];
  const outputs = data.step?.outputs ?? [];

  return (
    <NodeContainer nodeId={id} selected={selected} kind={data.kind} data={data}>
      <Handle type="target" position={Position.Left} style={circleHandleStyle('left', accent)} />
      <Handle type="source" position={Position.Right} style={circleHandleStyle('right', accent)} />
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {typeof data.step?.kind === 'string' ? data.step.kind : 'Transform'}
            </Typography>
            {data.step?.description && typeof data.step.description === 'string' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {data.step.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Transformation Type Icon */}
            {(() => {
              const op = (data.step?.operation || '').toLowerCase();
              const kind = (data.step?.kind || '').toLowerCase();
              let Icon = BuildIcon; // Default

              if (op.includes('join') || kind.includes('join')) Icon = LinkIcon;
              else if (op === 'direct' || kind === 'direct') Icon = ArrowForwardIcon;
              else if (op === 'filter' || kind === 'filter') Icon = FilterAltIcon;
              else if (op === 'aggregate' || kind === 'aggregate') Icon = FunctionsIcon;

              const tooltipTitle = op || kind || 'Transformation';

              return (
                <Tooltip title={`Operation: ${tooltipTitle}`}>
                  <Box sx={{ display: 'flex', color: alpha(accent, 0.7), alignItems: 'center', gap: 0.5 }}>
                    <Icon sx={{ fontSize: 16 }} />
                    {/* Show label if generic icon used and not direct */}
                    {Icon === BuildIcon && op !== 'direct' && (
                      <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>
                        {op.slice(0, 4)}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })()}
            {expression && (
              <Tooltip title="View transformation expression">
                <IconButton
                  size="small"
                  onClick={handleDialogOpen}
                  sx={{
                    border: `1px solid ${alpha(accent, 0.2)}`,
                    backgroundColor: alpha(accent, 0.08),
                    '&:hover': { backgroundColor: alpha(accent, 0.16) },
                  }}
                >
                  <CodeIcon sx={{ fontSize: 16, color: accent }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            Confidence {data.step?.confidence ? `${Math.round(data.step.confidence * 100)}%` : 'n/a'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {inputs.length} inputs
          </Typography>
        </Stack>
      </Stack>
      {dialogOpen && (
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          onClick={(event) => event.stopPropagation()}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {data.step?.kind ?? 'Transformation details'}
            </Typography>
            <IconButton size="small" onClick={handleDialogDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {data.step?.description && (
              <Typography variant="body2" color="text.secondary">
                {data.step.description}
              </Typography>
            )}
            {expression && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Expression
                </Typography>
                <Box
                  sx={{
                    backgroundColor: alpha('#1f2937', 0.04),
                    borderRadius: 1,
                    border: `1px solid ${alpha('#1f2937', 0.08)}`,
                    p: 1.5,
                    fontFamily: 'monospace',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {expression}
                </Box>
              </Box>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Inputs
                </Typography>
                {inputs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No explicit inputs recorded.
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {inputs.map((input, idx) => (
                      <Chip key={typeof input === 'string' ? input : idx} label={typeof input === 'string' ? input : String(input)} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Outputs
                </Typography>
                {outputs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No explicit outputs recorded.
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {outputs.map((output, idx) => (
                      <Chip key={`output-${typeof output === 'string' ? output : idx}`} label={typeof output === 'string' ? output : String(output)} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogDismiss} startIcon={<CloseIcon fontSize="small" />}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </NodeContainer>
  );
});

export const IntermediateFieldNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => (
  <NodeContainer nodeId={id} selected={selected} kind={data.kind} data={data}>
    <Handle type="target" position={Position.Left} style={circleHandleStyle('left', accentForKind('intermediateField'))} />
    <Handle type="source" position={Position.Right} style={circleHandleStyle('right', accentForKind('intermediateField'))} />
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {data.field}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Intermediate output
    </Typography>
  </NodeContainer>
));

export const AggregationNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const overlay = data.overlay;
  const accent = overlay?.color ?? accentForKind('aggregation');
  const aggregation = data.aggregation;
  const groupBy = aggregation?.groupBy ?? [];
  const inputRanges = aggregation?.inputRanges ?? [];
  const validatorChecks = aggregation?.validatorChecks ?? [];
  const statusChipColor =
    overlay?.validatorStatus === 'validated'
      ? 'success'
      : overlay?.validatorStatus === 'failed'
        ? 'error'
        : 'default';
  const statusLabel =
    overlay?.validatorStatus === 'validated'
      ? 'Validated'
      : overlay?.validatorStatus === 'failed'
        ? 'Failed'
        : overlay?.validatorStatus === 'pending'
          ? 'Pending'
          : undefined;

  return (
    <NodeContainer nodeId={id} selected={selected} kind="aggregation" data={data}>
      <Handle type="target" position={Position.Left} style={circleHandleStyle('left', accent)} />
      <Handle type="source" position={Position.Right} style={circleHandleStyle('right', accent)} />
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {overlay && (
              <Chip
                size="small"
                label={typeof overlay.badgeLabel === 'string' ? overlay.badgeLabel : String(overlay.badgeLabel ?? '')}
                sx={{
                  bgcolor: overlay.color,
                  color: '#fff',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              />
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {typeof aggregation?.operation === 'string' ? aggregation.operation : 'Aggregation'}
            </Typography>
          </Stack>
          {statusLabel && (
            <Chip
              size="small"
              color={statusChipColor}
              variant={statusChipColor === 'default' ? 'outlined' : 'filled'}
              label={statusLabel}
            />
          )}
        </Stack>
        {groupBy.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Group by {groupBy.join(', ')}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Chip
            size="small"
            variant="outlined"
            label={`${inputRanges.length} inputs`}
            sx={{ borderColor: accent, color: accent }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${aggregation?.outputFields?.length ?? 0} outputs`}
            sx={{ borderColor: accent, color: accent }}
          />
        </Stack>
        {validatorChecks.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Validators: {validatorChecks.length}
          </Typography>
        )}
      </Stack>
    </NodeContainer>
  );
});

export const LinkBridgeNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id }) => {
  const tone = accentForKind('linkBridge');
  return (
    <Box
      data-id={id}
      data-kind="linkBridge"
      sx={{
        position: 'relative',
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: alpha(tone, 0.18),
        border: `2px solid ${alpha(tone, 0.45)}`,
        boxShadow: `0 0 0 4px ${alpha(tone, 0.12)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <Handle type="target" position={Position.Left} style={circleHandleStyle('left', accentForKind('intermediateField'))} />
      <Handle type="source" position={Position.Right} style={circleHandleStyle('right', accentForKind('intermediateField'))} />
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: alpha(tone, 0.45),
          border: `2px solid ${alpha('#0f172a', 0.2)}`,
        }}
      />
    </Box>
  );
});

export const TableGroupNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const tone = data.groupRole === 'target' ? '#0891b2' : '#2563eb';
  const displayLabel = (data.label && data.label.trim().length > 0 ? data.label : undefined) ?? data.qualifiedName ?? 'Untitled Group';
  const secondaryLabel = data.qualifiedName;
  const [hovered, setHovered] = useState(false);

  // Phase 1 (P0): Hover-to-expand with delay
  // When user hovers a collapsed group for 500ms, auto-expand it.
  // When mouse leaves, collapse after 300ms (cancelled if mouse re-enters).
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverExpandedRef = useRef(false); // tracks whether WE triggered the expand via hover

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    // Cancel any pending collapse timer
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    // If collapsed, start expand timer
    if (data.groupExpanded === false && data.onToggleExpand) {
      expandTimerRef.current = setTimeout(() => {
        hoverExpandedRef.current = true;
        data.onToggleExpand?.();
      }, 500);
    }
  }, [data.groupExpanded, data.onToggleExpand]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    // Cancel any pending expand timer
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
    // If WE expanded it via hover, schedule a collapse
    if (hoverExpandedRef.current && data.groupExpanded !== false && data.onToggleExpand) {
      collapseTimerRef.current = setTimeout(() => {
        hoverExpandedRef.current = false;
        data.onToggleExpand?.();
      }, 300);
    }
  }, [data.groupExpanded, data.onToggleExpand]);

  // Sample rows for inline preview (passed via data.sampleRows)
  const sampleRows = data.sampleRows as Array<Record<string, unknown>> | undefined;
  // Show preview if: global toggle is ON (default) OR node is selected OR node is hovered
  const showInlinePreview = sampleRows && sampleRows.length > 0 && (data.showDataPreview !== false || selected || hovered);

  const columns = showInlinePreview
    ? Object.keys(sampleRows[0] || {}).slice(0, 4) // Limit to 4 columns
    : [];
  const displayRows = showInlinePreview ? sampleRows.slice(0, 2) : []; // Limit to 2 rows

  return (
    <Box
      data-id={id}
      data-kind="tableGroup"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '6px',
        border: `1.5px solid ${alpha(tone, selected ? 0.5 : 0.18)}`,
        backgroundColor: 'rgba(249,250,252,0.97)',
        boxShadow: selected
          ? `0 0 0 1px ${tone}, 0 8px 24px rgba(15, 23, 42, 0.10)`
          : `0 0 0 1px ${alpha(tone, 0.08)}, 0 4px 16px rgba(15, 23, 42, 0.06)`,
        position: 'relative',
        pointerEvents: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        ...(data.dimmed && { opacity: 0.35, filter: 'grayscale(0.4)' }),
      }}
    >
      {/* Handles for Group-level connections (e.g. Joins) */}
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        isConnectable={false}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: tone,
          border: `2px solid #fff`,
          left: -4,
          top: CANVAS_GROUP_HEADER_HEIGHT / 2,
          opacity: data.groupRole === 'target' ? 1 : 0, // Only visible for target groups
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        isConnectable={false}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: tone,
          border: `2px solid #fff`,
          right: -4,
          top: CANVAS_GROUP_HEADER_HEIGHT / 2,
          opacity: data.groupRole === 'source' ? 1 : 0, // Only visible for source groups
        }}
      />

      {/* Header Box - Always visible at top */}
      <Box
        sx={{
          minHeight: CANVAS_GROUP_HEADER_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: 2.5,
          borderBottom: `1px solid ${alpha(tone, 0.12)}`,
          backgroundColor: alpha(tone, 0.07),
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 15,
          pointerEvents: 'auto',
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: tone,
              border: `2px solid ${alpha('#fff', 0.8)}`,
              boxShadow: `0 1px 4px ${alpha(tone, 0.3)}`,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {data.groupRole === 'source' ? (
              <StorageIcon sx={{ fontSize: 13, color: '#fff' }} />
            ) : (
              <TableChartIcon sx={{ fontSize: 13, color: '#fff' }} />
            )}
          </Box>
          <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: tone,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
              }}
              title={displayLabel}
            >
              {displayLabel}
            </Typography>
            {secondaryLabel && secondaryLabel !== displayLabel && (
              <Typography
                variant="caption"
                sx={{
                  color: alpha(tone, 0.55),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  fontSize: 11,
                  fontWeight: 500,
                }}
                title={secondaryLabel}
              >
                {secondaryLabel}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Secondary actions — visible on hover only */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.15s ease',
                pointerEvents: hovered ? 'auto' : 'none',
              }}
            >
              {data.onDrillOut && (
                <Tooltip title="Back to pipeline overview" placement="bottom">
                  <span>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        data.onDrillOut?.();
                      }}
                      sx={{
                        border: `1px solid ${alpha(tone, 0.2)}`,
                        color: tone,
                        backgroundColor: alpha('#ffffff', 0.9),
                        '&:hover': {
                          backgroundColor: alpha(tone, 0.1),
                        },
                      }}
                    >
                      <VisibilityOffIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {data.onToggleExpand && (
                <Tooltip title={data.expanded ? 'Collapse group' : 'Expand group'} placement="bottom">
                  <span>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        data.onToggleExpand?.();
                      }}
                      sx={{
                        border: `1px solid ${alpha(tone, 0.2)}`,
                        color: tone,
                        backgroundColor: alpha('#ffffff', 0.9),
                        '&:hover': {
                          backgroundColor: alpha(tone, 0.1),
                        },
                      }}
                    >
                      {data.expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Preview sample rows for this table" placement="bottom">
                <span>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      data.previewActions?.openGroupPreview?.();
                    }}
                    disabled={!data.previewActions?.openGroupPreview}
                    sx={{
                      border: `1px solid ${alpha(tone, 0.2)}`,
                      color: tone,
                      backgroundColor: alpha('#ffffff', 0.9),
                      '&:hover': {
                        backgroundColor: alpha(tone, 0.1),
                      },
                    }}
                  >
                    <PreviewIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {/* Context menu — always visible */}
            <Tooltip title="Table actions" placement="bottom">
              <Box
                component="span"
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  data.onOpenContextMenu?.(event);
                }}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1.5,
                  border: `1.5px solid ${alpha(tone, 0.3)}`,
                  backgroundColor: alpha(tone, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: tone,
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                  cursor: data.onOpenContextMenu ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  '&:hover': data.onOpenContextMenu ? {
                    backgroundColor: alpha(tone, 0.18),
                    borderColor: alpha(tone, 0.5),
                  } : {},
                }}
              >
                {'≡'}
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
        {/* Join badges row — separate row below header content */}
        {data.joinBadges && data.joinBadges.length > 0 && (() => {
          const maxVisible = 3;
          const visible = data.joinBadges.slice(0, maxVisible);
          const overflow = data.joinBadges.length - maxVisible;
          return (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexWrap: 'nowrap',
                gap: 0.5,
                pl: 4.5,
                pb: 0.5,
                overflow: 'hidden',
              }}
            >
              {visible.map((badge, idx) => {
                const badgeColor = joinTypeColor(badge.joinType);
                const onClause = badge.joinKeys
                  .map(k => `${k.left || k.key || '?'} = ${k.right || k.value || '?'}`)
                  .join(', ');
                return (
                  <Tooltip
                    key={idx}
                    title={`${badge.joinType.toUpperCase()} JOIN with ${badge.otherTable} ON ${onClause} (${Math.round(badge.confidence * 100)}% confidence)`}
                  >
                    <Chip
                      size="small"
                      label={`${badge.joinType}→${badge.otherTable}`}
                      icon={
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: badgeColor,
                            display: 'inline-block',
                            flexShrink: 0,
                            ml: '4px !important',
                          }}
                        />
                      }
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 600,
                        backgroundColor: alpha(badgeColor, 0.1),
                        border: `1px solid ${alpha(badgeColor, 0.3)}`,
                        color: badgeColor,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Tooltip>
                );
              })}
              {overflow > 0 && (
                <Tooltip title={data.joinBadges.slice(maxVisible).map(b => `${b.joinType} JOIN → ${b.otherTable}`).join('\n')}>
                  <Chip
                    size="small"
                    label={`+${overflow} more`}
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 600,
                      backgroundColor: alpha(tone, 0.08),
                      border: `1px solid ${alpha(tone, 0.2)}`,
                      color: alpha(tone, 0.7),
                      flexShrink: 0,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          );
        })()}
      </Box>

      {/* Phase 1 (P0): Collapsed summary chip — shown when group is collapsed */}
      {data.groupExpanded === false && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.5,
            px: 2,
            pointerEvents: 'auto',
          }}
        >
          {data.status ? (
            <Chip
              size="small"
              label={data.status}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: data.status === 'Pending mapping'
                  ? alpha('#f59e0b', 0.12)
                  : data.status === 'profiling'
                    ? alpha('#8b5cf6', 0.12)
                    : alpha(tone, 0.08),
                border: `1px solid ${data.status === 'Pending mapping'
                  ? alpha('#f59e0b', 0.3)
                  : data.status === 'profiling'
                    ? alpha('#8b5cf6', 0.3)
                    : alpha(tone, 0.2)}`,
                color: data.status === 'Pending mapping'
                  ? '#b45309'
                  : data.status === 'profiling'
                    ? '#6d28d9'
                    : alpha(tone, 0.85),
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ) : (
            <Chip
              size="small"
              label={`${data.groupMappedCount ?? 0}/${data.groupTotalFieldCount ?? 0} fields mapped`}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: alpha(tone, 0.08),
                border: `1px solid ${alpha(tone, 0.2)}`,
                color: alpha(tone, 0.85),
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>
      )}

      {/* Inline Data Grid - Shows when sample rows are available */}
      {showInlinePreview && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderTop: `1px solid ${alpha(tone, 0.15)}`,
            px: 1.5,
            py: 1,
            zIndex: 10,
            pointerEvents: 'auto',
          }}
        >
          <Stack spacing={0.5}>
            {/* Column Headers */}
            <Stack direction="row" spacing={1} sx={{ borderBottom: `1px solid ${alpha(tone, 0.1)}`, pb: 0.5 }}>
              {columns.map((col) => (
                <Typography
                  key={col}
                  variant="caption"
                  sx={{
                    flex: 1,
                    fontWeight: 600,
                    color: tone,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={col}
                >
                  {col}
                </Typography>
              ))}
            </Stack>
            {/* Data Rows */}
            {displayRows.map((row, rowIdx) => (
              <Stack key={rowIdx} direction="row" spacing={1}>
                {columns.map((col) => {
                  const val = row[col];
                  const display = val === null || val === undefined ? '—' : String(val).slice(0, 12);
                  return (
                    <Typography
                      key={col}
                      variant="caption"
                      sx={{
                        flex: 1,
                        fontFamily: 'monospace',
                        fontSize: 10,
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={String(val ?? '')}
                    >
                      {display}
                    </Typography>
                  );
                })}
              </Stack>
            ))}
            {/* Row count badge */}
            <Typography variant="caption" sx={{ fontSize: 9, color: alpha(tone, 0.6), textAlign: 'right' }}>
              {sampleRows.length} sample rows
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
});

// ─── Venn Diagram SVG for Join Types ───────────────────────────────────────
const JoinTypeVenn: React.FC<{ type: JoinType; size?: number }> = ({ type, size = 20 }) => {
  const h = size * 0.8;
  const cx1 = size * 0.35;
  const cx2 = size * 0.65;
  const cy = h / 2;
  const r = size * 0.28;

  const leftId = `venn-left-${type}`;
  const rightId = `venn-right-${type}`;

  const fillLeft = type === 'left' || type === 'full' || type === 'cross';
  const fillRight = type === 'right' || type === 'full' || type === 'cross';
  const fillIntersection = type === 'inner' || type === 'left' || type === 'right' || type === 'full';
  const separated = type === 'cross';

  const actualCx2 = separated ? cx2 + r * 0.3 : cx2;
  const actualCx1 = separated ? cx1 - r * 0.3 : cx1;

  const color = joinTypeColor(type);

  return (
    <svg width={size} height={h} viewBox={`0 0 ${size} ${h}`}>
      {/* Left circle */}
      <circle cx={actualCx1} cy={cy} r={r} fill={fillLeft ? alpha(color, 0.3) : 'none'} stroke={color} strokeWidth={1.2} />
      {/* Right circle */}
      <circle cx={actualCx2} cy={cy} r={r} fill={fillRight ? alpha(color, 0.3) : 'none'} stroke={color} strokeWidth={1.2} />
      {/* Intersection highlight */}
      {fillIntersection && !separated && (
        <clipPath id={leftId}>
          <circle cx={actualCx1} cy={cy} r={r} />
        </clipPath>
      )}
      {fillIntersection && !separated && (
        <circle cx={actualCx2} cy={cy} r={r} fill={alpha(color, 0.55)} clipPath={`url(#${leftId})`} />
      )}
    </svg>
  );
};

// ─── JoinNode (Table-Level View) ───────────────────────────────────────────
export const JoinNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const join = data.join;
  const jt = join?.joinType ?? 'inner';
  const accent = joinTypeColor(jt);
  const confidence = data.confidence ?? 0;
  const confColor = confidence >= 0.9 ? '#10b981' : confidence >= 0.7 ? '#f59e0b' : '#ef4444';

  const onClause = join?.joinKeys
    ?.map(k => `${k.left} = ${k.right}`)
    .join(' AND ') ?? '';

  return (
    <Box
      data-id={id}
      data-kind="joinNode"
      sx={{
        position: 'relative',
        width: 200,
        borderRadius: 2,
        border: `1px solid ${alpha(accent, selected ? 0.7 : 0.25)}`,
        borderLeft: `4px solid ${accent}`,
        backgroundColor: '#ffffff',
        boxShadow: selected
          ? `0 8px 24px ${alpha(accent, 0.18)}, 0 0 0 2px ${accent}`
          : `0 2px 8px rgba(0,0,0,0.06)`,
        padding: '10px 12px',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: `0 6px 16px ${alpha(accent, 0.12)}`,
          borderColor: alpha(accent, 0.5),
        },
      }}
    >
      <Handle type="target" position={Position.Left} style={circleHandleStyle('left', accent)} />
      <Handle type="source" position={Position.Right} style={circleHandleStyle('right', accent)} />

      {/* Row 1: Venn + join type + confidence */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <JoinTypeVenn type={jt} size={22} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 12, color: accent, flex: 1 }}>
          {jt} Join
        </Typography>
        <Chip
          size="small"
          label={`${Math.round(confidence * 100)}%`}
          sx={{
            height: 18,
            fontSize: 10,
            fontWeight: 700,
            backgroundColor: alpha(confColor, 0.12),
            color: confColor,
            border: `1px solid ${alpha(confColor, 0.3)}`,
            '& .MuiChip-label': { px: 0.5 },
          }}
        />
      </Stack>

      {/* Row 2: ON clause */}
      {onClause && (
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontSize: 10,
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 0.25,
          }}
        >
          ON {onClause}
        </Typography>
      )}

      {/* Row 3: Table names */}
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
        {join?.leftFile ?? '?'} × {join?.rightFile ?? '?'}
      </Typography>
    </Box>
  );
});

// ─── TableNode (Table-Level View) ──────────────────────────────────────────
export const TableNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const isTarget = data.groupRole === 'target';
  const tone = isTarget ? '#0891b2' : '#2563eb';

  return (
    <Box
      data-id={id}
      data-kind="tableNode"
      sx={{
        position: 'relative',
        width: 200,
        borderRadius: 2,
        border: `2px solid ${alpha(tone, selected ? 0.6 : 0.25)}`,
        backgroundColor: '#ffffff',
        boxShadow: selected
          ? `0 8px 24px ${alpha(tone, 0.18)}, 0 0 0 2px ${tone}`
          : `0 2px 8px rgba(0,0,0,0.06)`,
        padding: '12px 14px',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: `0 6px 16px ${alpha(tone, 0.12)}`,
          borderColor: alpha(tone, 0.5),
        },
      }}
    >
      <Handle type="target" position={Position.Left} style={circleHandleStyle('left', tone)} />
      <Handle type="source" position={Position.Right} style={circleHandleStyle('right', tone)} />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <TableChartIcon sx={{ fontSize: 20, color: tone }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: tone,
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
          title={data.label}
        >
          {data.label}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1}>
        {data.columnCount != null && (
          <Chip
            size="small"
            label={`${data.columnCount} cols`}
            variant="outlined"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 600,
              borderColor: alpha(tone, 0.3),
              color: 'text.secondary',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
        {data.mappedCount != null && (
          <Chip
            size="small"
            label={`${data.mappedCount} mapped`}
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 600,
              backgroundColor: alpha(tone, 0.1),
              color: tone,
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
      </Stack>
    </Box>
  );
});

